import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  GraduationCap,
  BookOpen,
  Video,
  Award,
  ChevronRight,
  Target,
  PenTool,
  Trophy,
  Headphones,
  Mic,
  BookOpenCheck,
  Pencil,
  ArrowRight,
  Mail,
  Facebook,
  Instagram
} from 'lucide-react'

export default function Landing() {
  const { t } = useTranslation()

  const features = [
    {
      icon: BookOpen,
      title: 'Método paso a paso',
      description: 'Un programa estructurado en 16 lecciones progresivas para aprender francés de forma natural'
    },
    {
      icon: Target,
      title: '4 Habilidades',
      description: 'Escucha, habla, lectura y escritura trabajadas en cada lección'
    },
    {
      icon: PenTool,
      title: 'Práctica real',
      description: 'Ejercicios interactivos: dictado, escucha, redacción, emparejamiento y situaciones cotidianas'
    },
    {
      icon: Video,
      title: 'Talleres en vivo',
      description: 'Conversaciones grupales por videoconferencia para perder el miedo y ganar confianza'
    },
    {
      icon: Trophy,
      title: 'Motivación constante',
      description: 'Badges, rachas y recompensas que te impulsan a avanzar cada día'
    },
    {
      icon: Award,
      title: 'Certificado de Finalización',
      description: 'Certificado verificable de finalización que acredita que aprobaste todas las evaluaciones del programa'
    }
  ]

  const units = [
    {
      num: 1,
      title: 'Primeros Contactos',
      subtitle: 'Saludos, presentarse y preguntar cómo estás',
      lessons: ['Saludos y primer contacto', 'Presentarse', '¿Cómo estás?', 'Integración semana 1'],
      color: 'from-blue-500 to-blue-600'
    },
    {
      num: 2,
      title: 'Presentación Personal',
      subtitle: 'Edad, profesión, estudios y nacionalidad',
      lessons: ['Edad y números', 'Profesión y estudios', 'Origen y nacionalidad', 'Integración semana 2'],
      color: 'from-emerald-500 to-emerald-600'
    },
    {
      num: 3,
      title: 'Francés Comercial',
      subtitle: 'Comprar, vender y desenvolverse en situaciones cotidianas',
      lessons: ['Vender ropa y lentes', 'Farmacia y mercado', 'Joyería y abarrotes', 'Playa y direcciones'],
      color: 'from-amber-500 to-amber-600'
    },
    {
      num: 4,
      title: 'Hospitalidad',
      subtitle: 'Hotel, restaurante, atención al cliente y simulación real',
      lessons: ['Recepción de hotel: check-in', 'Restaurante: comandas y servicio', 'Atención al cliente variada', 'Simulación: tu día de trabajo'],
      color: 'from-purple-500 to-purple-600'
    }
  ]

  const skills = [
    {
      icon: Headphones,
      title: 'Escuchar',
      description: 'Comprensión auditiva con audios de hablantes nativos',
      color: 'bg-blue-100 text-blue-600'
    },
    {
      icon: Mic,
      title: 'Hablar',
      description: 'Ejercicios de pronunciación y expresión oral',
      color: 'bg-green-100 text-green-600'
    },
    {
      icon: BookOpenCheck,
      title: 'Leer',
      description: 'Comprensión de textos y vocabulario en contexto',
      color: 'bg-orange-100 text-orange-600'
    },
    {
      icon: Pencil,
      title: 'Escribir',
      description: 'Redacción, gramática y ortografía francesa',
      color: 'bg-purple-100 text-purple-600'
    }
  ]

  const steps = [
    {
      num: 1,
      title: 'Estudia el contenido',
      description: 'Teoría con audio y ejemplos prácticos de la vida real'
    },
    {
      num: 2,
      title: 'Práctica con ejercicios',
      description: 'Ejercicios variados: escucha, dictado, redacción, emparejamiento y más'
    },
    {
      num: 3,
      title: 'Participa en talleres',
      description: 'Conversaciones en vivo por videoconferencia cada semana'
    },
    {
      num: 4,
      title: 'Obtén tu certificado',
      description: 'Aprueba los exámenes y obtén tu certificado de finalización de francés aplicado'
    }
  ]

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-card/80 backdrop-blur-md border-b">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <GraduationCap className="w-8 h-8 text-primary" />
            <span className="text-xl font-bold">
              Nomad<span className="text-[#EF4135]">Lexis</span>
            </span>
          </Link>

          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="text-sm font-medium hover:text-primary transition-colors"
            >
              {t('auth.login')}
            </Link>
            <Link
              to="/planes"
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              {t('auth.signUp')}
            </Link>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4">
        <div className="container mx-auto text-center">
          <div className="flex justify-center gap-1 mb-8">
            <div className="w-8 h-12 bg-[#0055A4] rounded-l" />
            <div className="w-8 h-12 bg-white border-y" />
            <div className="w-8 h-12 bg-[#EF4135] rounded-r" />
          </div>

          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Aprende <span className="text-primary">Francés</span><br />Aplicado
          </h1>

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            Habla francés con NomadLexis en 16 lecciones con más de 350 ejercicios interactivos y talleres en vivo. Desde tu primer bonjour hasta conversar con fluidez.
          </p>

          <div className="flex justify-center mb-12">
            <Link
              to="/planes"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-primary text-primary-foreground rounded-xl text-lg font-semibold hover:bg-primary/90 transition-colors"
            >
              Comenzar ahora
              <ChevronRight className="w-5 h-5" />
            </Link>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-8 md:gap-12 text-center">
            <div>
              <div className="text-3xl font-bold text-primary">4</div>
              <div className="text-sm text-muted-foreground">Semanas</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary">16</div>
              <div className="text-sm text-muted-foreground">Lecciones</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary">64</div>
              <div className="text-sm text-muted-foreground">Contenidos</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-primary">384</div>
              <div className="text-sm text-muted-foreground">Ejercicios</div>
            </div>
          </div>

          {/* Certificate Badge */}
          <div className="mt-10 flex justify-center">
            <div className="inline-flex items-center gap-3 px-6 py-3 bg-gradient-to-r from-amber-50 to-yellow-50 border-2 border-amber-300 rounded-2xl shadow-md">
              <Award className="w-8 h-8 text-amber-500" />
              <div className="text-left">
                <div className="font-bold text-amber-700">
                  Certificado de Finalización
                </div>
                <div className="text-xs text-amber-600/80">
                  Verificable al completar el programa
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="container mx-auto">
          <p className="text-lg text-center text-muted-foreground max-w-2xl mx-auto">
            El problema no es el francés. Es todo lo que estás perdiendo por no hablarlo. Consíguelo en solo un mes
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mt-12">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-card rounded-xl p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-semibold mb-2">{feature.title}</h3>
                <p className="text-sm text-muted-foreground">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">
            ¿Cómo funciona?
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Un recorrido claro en 4 pasos para dominar el francés
          </p>

          <div className="max-w-4xl mx-auto grid md:grid-cols-4 gap-6">
            {steps.map((step, index) => (
              <div key={step.num} className="relative text-center">
                <div className="w-14 h-14 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4">
                  {step.num}
                </div>
                {index < steps.length - 1 && (
                  <ArrowRight className="hidden md:block absolute top-7 -right-3 w-5 h-5 text-muted-foreground" />
                )}
                <h3 className="font-semibold mb-2">{step.title}</h3>
                <p className="text-sm text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Mid */}
      <section className="py-16 px-4 bg-primary text-primary-foreground">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">
            ¿Listo para hablar francés?
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            Únete a cientos de estudiantes que ya están aprendiendo con NomadLexis
          </p>
          <Link
            to="/planes"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-primary rounded-xl text-lg font-semibold hover:bg-white/90 transition-colors"
          >
            Comenzar ahora
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Skills Section */}
      <section className="py-16 px-4 bg-muted/50">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">
            Las 4 habilidades
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            Cada lección desarrolla las 4 habilidades esenciales para comunicarte en francés
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {skills.map((skill, index) => (
              <div key={index} className="bg-card rounded-xl p-6 shadow-sm text-center">
                <div className={`w-14 h-14 ${skill.color} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <skill.icon className="w-7 h-7" />
                </div>
                <h3 className="font-semibold mb-2 text-lg">{skill.title}</h3>
                <p className="text-sm text-muted-foreground">{skill.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Program Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <h2 className="text-3xl font-bold text-center mb-4">
            Programa del curso
          </h2>
          <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
            4 semanas progresivas: desde las bases del francés hasta conversar con fluidez
          </p>

          <div className="max-w-3xl mx-auto space-y-4">
            {units.map((unit) => (
              <div
                key={unit.num}
                className="bg-card rounded-xl border hover:border-primary hover:shadow-md transition-all overflow-hidden"
              >
                <div className="flex items-center gap-4 p-5">
                  <div className={`w-14 h-14 bg-gradient-to-br ${unit.color} text-white rounded-2xl flex items-center justify-center font-bold text-xl shrink-0`}>
                    {unit.num}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-lg">{unit.title}</h3>
                    <p className="text-sm text-muted-foreground">{unit.subtitle}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs font-medium text-primary">
                      Semana {unit.num}
                    </div>
                    <div className="text-xs text-muted-foreground">4 lecciones</div>
                  </div>
                </div>
                <div className="px-5 pb-4 pt-0">
                  <div className="grid grid-cols-2 gap-1.5">
                    {unit.lessons.map((lesson, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary/40 shrink-0" />
                        {lesson}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-primary text-primary-foreground">
        <div className="container mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">
            ¿Listo para comenzar tu viaje?
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            Inscríbete y empieza a aprender francés hoy mismo.
          </p>
          <Link
            to="/planes"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-primary rounded-xl text-lg font-semibold hover:bg-white/90 transition-colors"
          >
            Inscribirse ahora
            <ChevronRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white border-t">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Column 1 - Brand & Contact */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <GraduationCap className="w-6 h-6 text-primary" />
                <span className="text-lg font-bold">
                  Nomad<span className="text-[#EF4135]">Lexis</span>
                </span>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Aprende francés en un mes
              </p>
              <a
                href="mailto:cfi.nomadlexis@gmail.com"
                className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-4"
              >
                <Mail className="w-4 h-4" />
                cfi.nomadlexis@gmail.com
              </a>
              <div className="flex items-center gap-3">
                <a
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-muted rounded-lg flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-white transition-colors"
                  aria-label="Facebook"
                >
                  <Facebook className="w-4 h-4" />
                </a>
                <a
                  href="#"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-9 h-9 bg-muted rounded-lg flex items-center justify-center text-muted-foreground hover:bg-primary hover:text-white transition-colors"
                  aria-label="Instagram"
                >
                  <Instagram className="w-4 h-4" />
                </a>
              </div>
            </div>

            {/* Column 2 - Program */}
            <div>
              <h4 className="font-semibold mb-4">
                Programa
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="#units" className="text-muted-foreground hover:text-primary transition-colors">
                    4 Unidades temáticas
                  </a>
                </li>
                <li>
                  <a href="#skills" className="text-muted-foreground hover:text-primary transition-colors">
                    4 Habilidades lingüísticas
                  </a>
                </li>
                <li>
                  <a href="#certificate" className="text-muted-foreground hover:text-primary transition-colors">
                    Certificado de finalización
                  </a>
                </li>
                <li>
                  <Link to="/planes" className="text-muted-foreground hover:text-primary transition-colors">
                    Inscribirse
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 3 - Legal */}
            <div>
              <h4 className="font-semibold mb-4">
                Legal
              </h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/privacidad" className="text-muted-foreground hover:text-primary transition-colors">
                    Política de Privacidad
                  </Link>
                </li>
                <li>
                  <Link to="/terminos" className="text-muted-foreground hover:text-primary transition-colors">
                    Términos y Condiciones
                  </Link>
                </li>
              </ul>
            </div>

            {/* Column 4 - Flags */}
            <div className="flex items-center justify-center md:justify-end gap-4">
              {/* France */}
              <div className="flex rounded-lg overflow-hidden shadow-sm w-[72px] h-[48px]">
                <div className="w-1/3 h-full bg-[#0055A4]" />
                <div className="w-1/3 h-full bg-white" />
                <div className="w-1/3 h-full bg-[#EF4135]" />
              </div>
              {/* Chile */}
              <div className="rounded-lg overflow-hidden shadow-sm w-[72px] h-[48px]">
                <div className="flex h-1/2">
                  <div className="w-1/3 h-full bg-[#0039A6] flex items-center justify-center">
                    <span className="text-white text-xs leading-none">&#9733;</span>
                  </div>
                  <div className="w-2/3 h-full bg-white" />
                </div>
                <div className="h-1/2 bg-[#D52B1E]" />
              </div>
            </div>
          </div>

          {/* Bottom bar */}
          <div className="mt-10 pt-6 border-t flex flex-col sm:flex-row items-center justify-between gap-2 text-xs text-muted-foreground">
            <p>© 2025 NomadLexis. Todos los derechos reservados.</p>
            <p>Hecho en Chile</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/56929237511?text=Hola%2C%20quiero%20m%C3%A1s%20informaci%C3%B3n%20sobre%20el%20programa%20de%20franc%C3%A9s"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#25D366] text-white rounded-full flex items-center justify-center shadow-lg hover:bg-[#1fb855] hover:scale-110 transition-all"
        aria-label="WhatsApp"
      >
        <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
      </a>
    </div>
  )
}
